import { type SelectOption } from '@framework/form/inputs/SelectInput.tsx';
import { useMemo } from 'react';
import { type IComponentOptions } from '../../../../../WidgetOptionLogic.ts';
import { type IWidgetParsedProps } from '../../util/getWidgetProps.ts';
import { parseOptionToSelect } from '../parseOptionToSelect.tsx';

/** Short per proprietà dei widget utilizzate nell'hook */
type UsedWidgetProps = Pick<IWidgetParsedProps, 'preview' | 'name'>;

/**
 * Hook che parsa le opzioni di un widget. Ritorna la lista da passare alla select.
 * Gestisce le regole di editabilità e visibilità
 */
export function useSelectOptions(
  /** Configurazione delle opzioni del widget */
  optionsConfig: IComponentOptions | null | undefined,
  /** proprietà del widget Select */
  parentWidgetProps: UsedWidgetProps,
) {
  const optionList = useMemo(() => {
    if (!optionsConfig || parentWidgetProps.preview) return [];
    const parsed = parseOptionToSelect(optionsConfig);
    return parsed;
  }, [optionsConfig, parentWidgetProps]);

  return optionList;
}

/**
 * Se l'opzione è invisibile, non la aggiunge
 * Se l'opzione è visibile, ma non abilitata, la aggiunge disabilitata
 * Se l'opzione è visibile e abilitata, la aggiunge
 */
function getSelectOption(
  option: SelectOption,
  enabledOptionsMap: Record<string, boolean>,
  visibleOptionsMap: Record<string, boolean>,
) {
  if (!option.value) return null;
  const enabled = enabledOptionsMap[option.value];
  const visible = visibleOptionsMap[option.value];

  if (!visible) return null;

  const result: SelectOption = {
    ...option,
    disabled: enabled === false,
  };
  return result;
}
